/**
 * @fileoverview Own the assigned attorney-review queue and selected review packet. Backend review records define assignment, evidence, and permitted decisions; this workspace owns navigation, filters, and explicit submission. `EngagementDraftPreview.js` supplies associated draft context and `useAssignedAttorneyReviewCount.js` refreshes the navigation badge. Review outcomes remain distinct from AI recommendations, draft generation, and final matter conversion.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ToastContext from '../../ToastContext';
import {
  fetchIntakeAttorneyReview,
  fetchIntakeAttorneyReviews,
  submitIntakeAttorneyReviewDecision,
} from './intakeApi';
import { EmptyState, IntakeBadge, TextArea } from './IntakeDetailPrimitives';
import { formatDate, titleize } from './intakeFormatters';
import {
  AssignedAttorneyReviewCountChip,
  publishAssignedAttorneyReviewCount,
  refreshAssignedAttorneyReviewCount,
  useAssignedAttorneyReviewCount,
} from './useAssignedAttorneyReviewCount';
import AppIcon from '../shared/AppIcon';
import EngagementDraftPreview from './EngagementDraftPreview';
import IntakeViewSwitch from './IntakeViewSwitch';
import { readIntakeViewState, writeIntakeViewState } from './intakeWorkspaceState';
import './Intake.css';

const REVIEW_TABS = [
  { key: 'assigned', label: 'Assigned to Me' },
  { key: 'open', label: 'Open' },
  { key: 'decided', label: 'Decided' },
];

const REVIEW_TAB_KEYS = REVIEW_TABS.map((tab) => tab.key);
const REVIEW_VIEW_STORAGE_KEY = 'intake_attorney_review_view';

const REVIEW_SORT_OPTIONS = [
  { key: 'assigned_desc', label: 'Assigned newest', field: 'assigned', order: -1 },
  { key: 'assigned_asc', label: 'Assigned oldest', field: 'assigned', order: 1 },
  { key: 'lead_asc', label: 'Lead A-Z', field: 'lead', order: 1 },
  { key: 'status_asc', label: 'Status', field: 'status', order: 1 },
];

const DECISION_ACTIONS = [
  { status: 'needs_more_info', label: 'Need More Info', icon: 'pi pi-info-circle', className: '' },
  { status: 'rejected', label: 'Reject Case', icon: 'pi pi-times', className: 'intake-button--danger' },
  { status: 'approved', label: 'Approve Case', icon: 'pi pi-check', className: 'intake-button--primary' },
];



function normalizeReviewView(value) {
  return REVIEW_TAB_KEYS.includes(value) ? value : null;
}



function readStoredReviewView() {
  try {
    return normalizeReviewView(window.localStorage.getItem(REVIEW_VIEW_STORAGE_KEY));
  } catch {
    return null;
  }
}



function writeStoredReviewView(view) {
  try {
    window.localStorage.setItem(REVIEW_VIEW_STORAGE_KEY, view);
  } catch (error) {
    console.warn(`Failed to persist ${REVIEW_VIEW_STORAGE_KEY}:`, error);
  }
}



function compactDate(value) {
  return value ? formatDate(value) : '-';
}



function sortKeyForState(sortState) {
  const option = REVIEW_SORT_OPTIONS.find((item) => item.field === sortState.field && item.order === sortState.order);
  return option?.key || REVIEW_SORT_OPTIONS[0].key;
}



function sortStateForKey(key) {
  const option = REVIEW_SORT_OPTIONS.find((item) => item.key === key) || REVIEW_SORT_OPTIONS[0];
  return { field: option.field, order: option.order };
}



function reviewLead(review) {
  return review.lead || {};
}



function reviewTitle(review) {
  const lead = reviewLead(review);
  return lead.prospect_name || lead.lead_title || `Lead #${review.lead_id}`;
}



function routedAttorneyName(review) {
  if (review.attorney_name) return review.attorney_name;
  if (review.attorney_email) return review.attorney_email;
  return review.is_firm_review ? 'Firm Case Review' : '-';
}



function reviewDefendant(review) {
  const lead = reviewLead(review);
  return lead.defendant_name_raw || lead.defendant_name_normalized || '-';
}



function reviewForum(review) {
  const lead = reviewLead(review);
  return [lead.state_events, lead.state_client, lead.state_defendant].filter(Boolean).join(' / ') || '-';
}



function reviewDocumentCount(review) {
  return Number(reviewLead(review).document_count || 0);
}



function reviewNewDocumentCount(review) {
  return Number(reviewDocumentSummary(review).new_document_count || 0);
}



function reviewReasonsText(review) {
  return (review.match_reasons || []).filter(Boolean).join(', ') || '-';
}



function reviewActionLabel(review) {
  if (review?.lead?.status === 'converted') return 'Resolved elsewhere';
  return review.status === 'pending' ? 'Action needed' : titleize(review.status);
}



function AttorneyReviewStateBadge({ review, lead }) {
  if (review.status === 'pending' && lead?.status === 'converted') {
    return <IntakeBadge value="Resolved elsewhere" className="intake-badge--muted" />;
  }
  if (review.status === 'pending') {
    return <IntakeBadge value="awaiting_attorney_decision" className="intake-badge--info" />;
  }
  if (review.status === 'approved' && lead?.status === 'engagement_letter_to_be_sent') {
    return <IntakeBadge value="Accepted — Engagement Pending" className="intake-badge--info" />;
  }
  return <IntakeBadge value={review.status} />;
}



function reviewPacketSummary(review) {
  return casePresentationText(review.case_presentation) || '-';
}



function formatDocumentCount(count) {
  return `${count} ${count === 1 ? 'document' : 'documents'}`;
}



function formatNewDocumentCount(count) {
  return `${count} new`;
}



function displayValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ') || '-';
  if (value && typeof value === 'object') return Object.entries(value).map(([key, item]) => `${titleize(key)}: ${displayValue(item)}`).join('\n');
  return value || '-';
}



function casePresentationText(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(casePresentationText).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    const prioritized = ['headline', 'summary', 'pitch', 'why_this_case', 'review_focus', 'key_facts', 'risks'];
    const rows = prioritized
    .filter((key) => value[key])
    .map((key) => `${titleize(key)}: ${displayValue(value[key])}`);
    if (rows.length) return rows.join('\n');
    return '';
  }
  return String(value).trim();
}



function casePresentationForReview(review, recommendation) {
  return casePresentationText(review?.case_presentation) || recommendation?.summary || '';
}



function handoffNoteForReview(review) {
  const presentation = review?.case_presentation;
  return presentation && typeof presentation === 'object' && !Array.isArray(presentation)
    ? presentation.handoff_note || ''
    : '';
}



function handoffEvidenceForReview(review) {
  const presentation = review?.case_presentation;
  if (!presentation || typeof presentation !== 'object' || Array.isArray(presentation)) return [];
  return Array.isArray(presentation.available_evidence)
    ? presentation.available_evidence.filter((item) => item?.stage_label && item?.summary)
    : [];
}



function handoffStageForReview(review) {
  const presentation = review?.case_presentation;
  return presentation && typeof presentation === 'object' && !Array.isArray(presentation)
    ? presentation.handoff_stage || ''
    : '';
}



function normalizeTextBlock(value) {
  return String(value || '')
  .replace(/\r\n/g, '\n')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();
}



function uniqueTextBlocks(values) {
  const seen = new Set();
  return values
  .map(normalizeTextBlock)
  .filter(

  (text) => {
    if (!text) return false;
    const key = text.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}



function caseReviewSummaryText(review, recommendation, lead) {
  const parts = uniqueTextBlocks([
    casePresentationForReview(review, recommendation),
    recommendation?.summary,
    lead?.staff_review_reason,
  ]);
  return parts.join('\n\n') || 'No case review summary is available yet.';
}



function issueTextItems(value) {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) return value.flatMap(issueTextItems);
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) => {
      return issueTextItems(item).map((text) => `${titleize(key)}: ${text}`);
    });
  }
  const text = normalizeTextBlock(value);
  return text && text !== '-' ? [text] : [];
}



function caseReviewIssueItems({ strengths, reviewKeyIssues, weaknesses, missingInfo, contradictions }) {
  const groups = [
    { label: 'Issue', value: reviewKeyIssues },
    { label: 'Strength', value: strengths },
    { label: 'Risk', value: weaknesses },
    { label: 'Missing Info', value: missingInfo },
    { label: 'Contradiction', value: contradictions },
  ];
  const seen = new Set();
  return groups
  .flatMap((group) => issueTextItems(group.value).map((text) => ({ label: group.label, text })))
  .filter(

  (item) => {
    const key = item.text.replace(/\s+/g, ' ').toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  })
  .slice(0, 6);
}



function latestRecommendation(recommendations = []) {
  return recommendations?.[0] || null;
}



function recommendationField(recommendation, keys) {
  const rationale = recommendation?.rationale || {};
  for (const key of keys) {
    if (recommendation?.[key]) return recommendation[key];
    if (rationale?.[key]) return rationale[key];
  }
  return '';
}



function ReviewReasonList({ reasons = [] }) {
  if (!reasons.length) return <strong>-</strong>;
  return (
    <ul className="intake-attorney-review-reasons">
      {reasons.map((reason, index) => <li key={`${reason}-${index}`}>{reason}</li>)}
    </ul>
  );
}



function reviewDocumentSummary(review, documentSummary = {}) {
  return review?.new_document_summary || documentSummary?.new_document_summary || {};
}



function reviewDocumentRows(summary = {}) {
  return summary.new_documents?.length ? summary.new_documents : summary.recent_documents || [];
}



function documentSummaryDetail(document) {
  if (document.findings) return document.findings;
  if (document.review_notes) return document.review_notes;
  return `${titleize(document.review_status || 'pending')} ${titleize(document.status || 'document')}`.trim();
}



function AttorneyDocumentSummary({ summary = {} }) {
  const documents = reviewDocumentRows(summary);
  if (!documents.length) return <EmptyState title="No new documents found." />;
  return (
    <div className="intake-attorney-review-status-list">
      {documents.map((document) => (
        <div className="intake-attorney-review-status-row" key={document.id || document.google_drive_file_id || document.title}>
          <div>
            {document.file_url ? (
              <a href={document.file_url} target="_blank" rel="noreferrer">{document.title || 'Document'}</a>
            ) : (
              <strong>{document.title || 'Document'}</strong>
            )}
            <span>{documentSummaryDetail(document)}</span>
          </div>
          <IntakeBadge value={document.review_status || document.status || 'pending'} />
        </div>
      ))}
    </div>
  );
}



function ReviewSortMenu({ value, onChange }) {
  return (
    <label className="intake-attorney-review-sort">
      <span>Sort</span>
      <select aria-label="Sort case reviews" value={value} onChange={(event) => onChange(event.target.value)}>
        {REVIEW_SORT_OPTIONS.map((option) => (
          <option key={option.key} value={option.key}>{option.label}</option>
        ))}
      </select>
    </label>
  );
}



function ReviewPacketMeta({ icon, label, value, className = '' }) {
  return (
    <span className={`intake-review-packet-meta${className ? ` ${className}` : ''}`}>
      <AppIcon icon={icon} aria-hidden="true" />
      <span className="intake-review-packet-meta__label">{label}</span>
      <strong>{value}</strong>
    </span>
  );
}



function ReviewPacketCard({
  review,
  selected = false,
  onOpenPacket,
}) {
  const lead = reviewLead(review);
  const documentCount = reviewDocumentCount(review);
  const newDocumentCount = reviewNewDocumentCount(review);

  return (
    <article
      className={`intake-review-packet${selected ? ' intake-review-packet--selected' : ''}`}
      role="listitem"
      data-testid="attorney-review-packet">
      <button
        type="button"
        className="intake-review-packet__summary-button"
        aria-label={`Open case review packet for ${reviewTitle(review)}`}
        aria-current={selected ? 'true' : undefined}
        onClick={() => onOpenPacket(review.id)}>
        <span className="intake-review-packet__header">
          <span className="intake-review-packet__title-group">
            <span className="intake-review-packet__title">{reviewTitle(review)}</span>
            <span className="intake-review-packet__summary">{reviewPacketSummary(review)}</span>
          </span>
          <span className="intake-review-packet__status">
            <strong>{reviewActionLabel(review)}</strong>
            <span>Review and decide</span>
          </span>
        </span>
        <span className="intake-review-packet__badges">
          {review.status !== 'pending' && <AttorneyReviewStateBadge review={review} lead={lead} />}
          {lead.potential_class_action && <span className="intake-badge intake-badge--violet">Potential Class Action</span>}
          {lead.category && <span className="intake-badge intake-badge--muted">{titleize(lead.category)}</span>}
        </span>
        <span className="intake-review-packet__meta">
          <ReviewPacketMeta icon="pi pi-building" label="Defendant" value={reviewDefendant(review)} />
          <ReviewPacketMeta icon="pi pi-map-marker" label="State / Forum" value={reviewForum(review)} />
          <ReviewPacketMeta icon="pi pi-file" label="Documents" value={formatDocumentCount(documentCount)} />
          <ReviewPacketMeta
            icon="pi pi-inbox"
            label="New"
            value={formatNewDocumentCount(newDocumentCount)}
            className={newDocumentCount > 0 ? 'intake-review-packet-meta--active' : ''} />
          <ReviewPacketMeta icon="pi pi-calendar" label="Assigned" value={compactDate(review.assigned_at)} />
          <ReviewPacketMeta icon="pi pi-user" label="Attorney" value={routedAttorneyName(review)} />
        </span>
        <span className="intake-review-packet__reason">
          <span>Why assigned</span>
          <strong>{reviewReasonsText(review)}</strong>
        </span>
      </button>
    </article>
  );
}



function ReviewPacketInbox({
  reviews,
  loading,
  activeReviewId,
  onOpenPacket,
}) {
  if (loading) {
    return <div className="intake-review-packet-list"><EmptyState title="Loading attorney reviews..." /></div>;
  }

  if (!reviews.length) {
    return <div className="intake-review-packet-list"><EmptyState title="No attorney reviews found." /></div>;
  }

  return (
    <div className="intake-review-packet-list" role="list" aria-label="Case review packets">
      {reviews.map((review) => (
        <ReviewPacketCard
          key={review.id}
          review={review}
          selected={String(activeReviewId || '') === String(review.id)}
          onOpenPacket={onOpenPacket} />
      ))}
    </div>
  );
}



export function IntakeAttorneyReviews() {
  const navigate = useNavigate();
  const location = useLocation();
  const { reviewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const showToast = useContext(ToastContext);
  const restoredStateRef = useRef(readIntakeViewState('reviews'));
  const restoredState = restoredStateRef.current;
  const requestedView = normalizeReviewView(searchParams.get('view'));
  const [view, setView] = useState(() => requestedView || readStoredReviewView() || 'assigned');
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [classActionOnly, setClassActionOnly] = useState(() => restoredState.classActionOnly === true);
  const [sortState, setSortState] = useState(() => sortStateForKey(restoredState.sortKey));
  const { count: assignedReviewCount } = useAssignedAttorneyReviewCount();
  const reviewQueueSearch = location.search || `?view=${encodeURIComponent(view)}`;

  useEffect(() => {
    if (requestedView && requestedView !== view) {
      setView(requestedView);
    }
  }, [requestedView, view]);

  useEffect(

  () => {
    if (requestedView && requestedView !== view) {
      return;
    }

    writeStoredReviewView(view);

    const currentRawView = searchParams.get('view');
    if (!currentRawView && view === 'assigned') {
      return;
    }

    if (currentRawView === view) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('view', view);
    setSearchParams(nextSearchParams, {
      replace: true,
      state: location.state,
    });
  }, [location.state, requestedView, searchParams, setSearchParams, view]);

  const loadReviews = useCallback(

  () => {
    setLoading(true);
    setError('');
    const params = {
      view,
      page: 1,
      page_size: 50,
      sort: sortState.field,
      direction: sortState.order === 1 ? 'asc' : 'desc',
    };
    if (classActionOnly) {
      params.potential_class_action = 'true';
    }
    return fetchIntakeAttorneyReviews(params)
    .then(

    (response) => {
      const nextTotal = response.data.total || 0;
      const nextReviews = response.data.reviews || [];
      setReviews(nextReviews);
      setTotal(nextTotal);
      if (view === 'assigned' && !classActionOnly) {
        publishAssignedAttorneyReviewCount(nextTotal);
      }
    })
    .catch((err) => {
      console.error('Failed to load attorney reviews:', err);
      setError('Unable to load attorney reviews.');
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load attorney reviews' });
    })
    .finally(() => setLoading(false));
  }, [classActionOnly, showToast, sortState.field, sortState.order, view]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    writeIntakeViewState('reviews', {
      classActionOnly,
      sortKey: sortKeyForState(sortState),
    });
  }, [classActionOnly, sortState]);

  const handleViewChange = useCallback(

  (nextView) => {
    const normalizedView = normalizeReviewView(nextView);
    if (!normalizedView) return;

    setView(normalizedView);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('view', normalizedView);
    setSearchParams(nextSearchParams, {
      state: location.state,
    });
  }, [location.state, searchParams, setSearchParams]);

  const handleSortChange = useCallback(

  (sortKey) => {
    setSortState(sortStateForKey(sortKey));
  }, []);

  const selectReview = useCallback(

  (nextReviewId) => {
    navigate(`/intake/reviews/${nextReviewId}${reviewQueueSearch}`, {
      state: {
        ...(location.state || {}),
        attorneyReviewPanel: true,
      },
    });
  }, [location.state, navigate, reviewQueueSearch]);

  const clearSelectedReview = useCallback(

  () => {
    navigate(`/intake/reviews${location.search || ''}`, { replace: true });
  }, [location.search, navigate]);

  return (
    <div className="intake-page">
      <div className="intake-page__body">
        <section className="intake-queue intake-attorney-review-queue">
          <div className="intake-toolbar intake-attorney-review-toolbar">
            <div className="intake-toolbar__primary">
              <div className="intake-title">
                <IntakeViewSwitch activeView="reviews" assignedReviewCount={assignedReviewCount} />
                <span className="intake-title__count">{total.toLocaleString()} reviews</span>
              </div>
              <button
                type="button"
                className={`intake-button${classActionOnly ? ' intake-button--primary' : ''}`}
                aria-pressed={classActionOnly}
                onClick={() => setClassActionOnly((current) => !current)}>
                <AppIcon icon="pi pi-filter" aria-hidden="true" />
                <span>Potential Class Actions</span>
              </button>
            </div>
            <div className="intake-attorney-review-tabs" role="tablist" aria-label="Attorney review filters">
              {REVIEW_TABS.map((tab) => (
                <button
                  type="button"
                  className={`intake-review-tab${view === tab.key ? ' intake-review-tab--active' : ''}`}
                  key={tab.key}
                  onClick={() => handleViewChange(tab.key)}>
                  <span>{tab.label}</span>
                  {tab.key === 'assigned' && (
                    <AssignedAttorneyReviewCountChip
                      count={assignedReviewCount}
                      testId="assigned-review-tab-count" />
                  )}
                </button>
              ))}
            </div>
            <div className="intake-toolbar__actions">
              <ReviewSortMenu
                value={sortKeyForState(sortState)}
                onChange={handleSortChange} />
              <button type="button" className="intake-button" onClick={loadReviews}>
                <AppIcon icon="pi pi-refresh" aria-hidden="true" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
          {error && <div className="intake-inline-alert" role="alert"><span>{error}</span></div>}
          <div className="intake-review-workspace" data-testid="attorney-review-inbox">
            <div className="intake-review-workspace__list">
              <ReviewPacketInbox
                reviews={reviews}
                loading={loading}
                activeReviewId={reviewId}
                onOpenPacket={selectReview} />
            </div>
            <aside
              className="intake-review-workspace__detail"
              {...(reviewId ? { role: 'dialog', 'aria-labelledby': 'intake-attorney-review-detail-title' } : { role: 'region', 'aria-label': 'Selected case review' })}>
              {reviewId ? (
                <IntakeAttorneyReviewDetailContent
                  reviewId={reviewId}
                  variant="panel"
                  onClose={clearSelectedReview}
                  onDecisionSubmitted={loadReviews} />
              ) : (
                <EmptyState title="Select a case review to see the packet and record a decision." />
              )}
            </aside>
          </div>
        </section>
      </div>
    </div>
  );
}



function IntakeAttorneyReviewDetailContent({ reviewId, variant = 'page', onClose, onDecisionSubmitted }) {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useContext(ToastContext);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState('');
  const [form, setForm] = useState({ reason: '', notes: '' });
  const [formError, setFormError] = useState('');
  const isDrawer = variant === 'drawer';
  const isEmbedded = isDrawer || variant === 'panel';
  const resolvedReviewId = String(reviewId || '');

  const closeDetail = useCallback(

  () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(`/intake/reviews${location.search || ''}`);
  }, [location.search, navigate, onClose]);

  const loadReview = useCallback(

  () => {
    if (!resolvedReviewId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchIntakeAttorneyReview(resolvedReviewId)
    .then((response) => setPayload(response.data))
    .catch((err) => {
      console.error('Failed to load attorney review:', err);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load attorney review' });
    })
    .finally(() => setLoading(false));
  }, [resolvedReviewId, showToast]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const submitDecision =

  async (status) => {
    const reason = form.reason.trim();
    if (!reason) {
      setFormError('Reason is required.');
      return;
    }
    setSavingStatus(status);
    try {
      const response = await submitIntakeAttorneyReviewDecision(resolvedReviewId, {
        status,
        reason,
        notes: form.notes.trim(),
      });
      setPayload((current) => current ? {
        ...current,
        review: response.data.attorney_review,
        lead: response.data.lead || current.lead,
        conversion: response.data.conversion || current.conversion,
        attorney_reviews: (current.attorney_reviews || []).map((item) => item.id === response.data.attorney_review.id ? response.data.attorney_review : item),
      } : current);
      setForm({ reason: '', notes: '' });
      setFormError('');
      const conversionMode = response.data.conversion?.mode;
      const matterNumber = response.data.conversion?.matter_number;
      const leadStatus = response.data.lead?.status;
      showToast({
        severity: 'success',
        summary: 'Saved',
        detail: leadStatus === 'engagement_letter_to_be_sent' ?
          'Case accepted; the engagement package is ready for staff follow-through' :
          conversionMode === 'pipedrive_evaluation_documents_handoff' ?
          'Case approved and assigned to Kimberly for document evaluation' :
          conversionMode === 'engagement_letter_handoff' ?
          'Case approved and marked engagement letter to be sent' :
          matterNumber ? `Case approved and converted to matter ${matterNumber}` : 'Attorney review decision recorded',
      });
      refreshAssignedAttorneyReviewCount();
      if (onDecisionSubmitted) {
        onDecisionSubmitted(response.data);
      }
    } catch (err) {
      console.error('Failed to submit attorney review decision:', err);
      showToast({ severity: 'error', summary: 'Error', detail: err?.response?.data?.error || 'Failed to record decision' });
    } finally {
      setSavingStatus('');
    }
  };

  if (loading) {
    if (isEmbedded) {
      return <div className="intake-attorney-review-detail-content intake-attorney-review-detail-content--drawer"><div className="intake-loading">Loading attorney review...</div></div>;
    }
    return <div className="intake-page"><div className="intake-page__body"><div className="intake-loading">Loading attorney review...</div></div></div>;
  }

  if (!payload?.review) {
    if (isEmbedded) {
      return (
        <div className="intake-attorney-review-detail-content intake-attorney-review-detail-content--drawer">
          <header className="intake-detail-page-header intake-detail-page-header--drawer">
            <button type="button" className="intake-button intake-button--icon" aria-label="Close case review" onClick={closeDetail}>
              <AppIcon icon="pi pi-times" aria-hidden="true" />
            </button>
          </header>
          <section className="intake-detail-panel"><EmptyState title="Attorney review not found." /></section>
        </div>
      );
    }
    return (
      <div className="intake-page">
        <div className="intake-page__body">
          <section className="intake-detail-panel"><EmptyState title="Attorney review not found." /></section>
        </div>
      </div>
    );
  }

  const { review, lead = {}, document_summary: documentSummary = {} } = payload;
  const recommendation = latestRecommendation(payload.final_recommendations);
  const strengths = recommendationField(recommendation, ['strengths', 'key_strengths']);
  const weaknesses = recommendationField(recommendation, ['weaknesses', 'key_weaknesses']);
  const reviewKeyIssues = recommendationField(recommendation, ['key_issues', 'issues', 'risk_flags', 'proof_issues', 'adverse_facts']);
  const missingInfo = recommendationField(recommendation, ['missing_information', 'missing_info', 'information_needed']);
  const contradictions = recommendationField(recommendation, ['document_contradictions', 'contradictions']);
  const forum = recommendationField(recommendation, ['fee_shifting_path', 'forum', 'arbitration', 'recommended_forum']);
  const caseReviewSummary = caseReviewSummaryText(review, recommendation, lead);
  const decisionFactors = caseReviewIssueItems({ strengths, reviewKeyIssues, weaknesses, missingInfo, contradictions });
  const newDocumentSummary = reviewDocumentSummary(review, documentSummary);
  const newDocumentCount = Number(newDocumentSummary.new_document_count || 0);
  const documentCount = documentSummary.document_count || lead.document_count || 0;
  const handoffNote = handoffNoteForReview(review);
  const handoffEvidence = handoffEvidenceForReview(review);
  const handoffStage = handoffStageForReview(review);
  const stateForum = [lead.state_events, lead.state_client, lead.state_defendant].filter(Boolean).join(' / ') || '-';
  const reviewResolvedElsewhere = lead.status === 'converted' && review.status === 'pending';

  const detailBody = (
    <div className={`intake-attorney-review-detail-content${isEmbedded ? ' intake-attorney-review-detail-content--drawer' : ''}`}>
        <header className="intake-detail-page-header">
          <div className="intake-detail-header-main">
            <button
              type="button"
              className={`intake-button${isEmbedded ? ' intake-button--icon' : ''}`}
              aria-label={isEmbedded ? 'Close case review' : undefined}
              onClick={closeDetail}>
              <AppIcon icon={isEmbedded ? 'pi pi-times' : 'pi pi-arrow-left'} aria-hidden="true" />
              {!isEmbedded && <span>Back</span>}
            </button>
            <div className="intake-detail-title">
              <span className="intake-detail-title__eyebrow">Case Review #{review.id}</span>
              <h1 id="intake-attorney-review-detail-title">{lead.prospect_name || lead.lead_title || `Lead #${review.lead_id}`}</h1>
            </div>
          </div>
          <div className="intake-attorney-review-header-actions">
            <Link to={`/intake/leads/${review.lead_id}`} className="intake-button intake-attorney-review-open-lead">
              <AppIcon icon="pi pi-external-link" aria-hidden="true" />
              <span>Open Lead</span>
            </Link>
            <div className="intake-detail-badges">
              <AttorneyReviewStateBadge review={review} lead={lead} />
              {lead.potential_class_action && <span className="intake-badge intake-badge--violet">Potential Class Action</span>}
            </div>
          </div>
        </header>

        <main className="intake-attorney-review-sidebar">
          <section className="intake-detail-panel intake-attorney-review-summary-panel">
            <div className="intake-section-heading">
              <h2>Case Review Summary</h2>
              <span>{recommendation ? titleize(recommendation.classification || recommendation.recommended_action) : `Evidence through ${handoffStage || titleize(lead.current_stage)}`}</span>
            </div>
            <p className="intake-attorney-review-summary">{caseReviewSummary}</p>
            {!recommendation && (
              <div className="intake-attorney-review-issues" aria-label="Available intake evidence">
                <div className="intake-attorney-review-issues__heading">
                  <AppIcon icon="pi pi-file" aria-hidden="true" />
                  <span>Available intake evidence</span>
                </div>
                {handoffEvidence.length ? <ul className="intake-attorney-review-issue-list">
                  {handoffEvidence.map((item, index) => <li className="intake-attorney-review-issue" key={`${item.stage_label}-${index}`}><span>{item.stage_label}</span><strong>{item.summary}</strong></li>)}
                </ul> : <p>The handoff occurred before Final Evaluation. Completed stage outputs remain available on the lead timeline.</p>}
              </div>
            )}
            {decisionFactors.length > 0 && (
              <div className="intake-attorney-review-issues">
                <div className="intake-attorney-review-issues__heading">
                  <AppIcon icon="pi pi-exclamation-circle" aria-hidden="true" />
                  <span>Decision Factors</span>
                </div>
                <ul className="intake-attorney-review-issue-list">
                  {decisionFactors.map((issue, index) => (
                    <li className="intake-attorney-review-issue" key={`${issue.label}-${issue.text}-${index}`}>
                      <span>{issue.label}</span>
                      <strong>{issue.text}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section className="intake-detail-panel">
            <div className="intake-section-heading"><h2>Decision Snapshot</h2></div>
            <div className="intake-attorney-review-facts intake-attorney-review-facts--sidebar">
              <div><span>Why Assigned</span><ReviewReasonList reasons={review.match_reasons || []} /></div>
              {handoffNote && <div><span>Handoff Note</span><strong>{handoffNote}</strong></div>}
              <div><span>Claim</span><strong>{titleize(lead.category)}</strong></div>
              <div><span>Defendant</span><strong>{lead.defendant_name_raw || lead.defendant_name_normalized || '-'}</strong></div>
              <div><span>State / Forum</span><strong>{stateForum}</strong></div>
              <div><span>Documents</span><strong>{documentCount}</strong></div>
              {newDocumentCount > 0 && <div><span>New Documents</span><strong>{newDocumentCount}</strong></div>}
              <div><span>Damages</span><strong>{lead.damages_reported || '-'}</strong></div>
              <div><span>Settlement / Fee / Forum</span><strong>{displayValue(forum)}</strong></div>
            </div>
          </section>

          {newDocumentCount > 0 && <section className="intake-detail-panel">
            <div className="intake-section-heading">
              <h2>New Documents</h2>
              <span>{newDocumentSummary.anchor_source ? `Since ${titleize(newDocumentSummary.anchor_source)}` : 'Recent uploads'}</span>
            </div>
            <AttorneyDocumentSummary summary={newDocumentSummary} />
          </section>}

          {payload.engagement_draft && (
            <details className="intake-detail-panel" open={review.status === 'approved'}>
              <summary>Engagement package</summary>
              <EngagementDraftPreview draft={payload.engagement_draft} onRefresh={loadReview} />
            </details>
          )}

          <section className="intake-detail-panel">
            <div className="intake-section-heading">
              <h2>Decision</h2>
              <span>{review.attorney_name || review.attorney_email}</span>
            </div>
            {reviewResolvedElsewhere ? (
              <div className="intake-inline-alert" role="status">
                <span>This lead has already converted. No Case Review decision is needed.</span>
              </div>
            ) : review.status !== 'pending' ? (
              <div className="intake-attorney-review-facts intake-attorney-review-facts--sidebar">
                <div><span>Outcome</span><strong>{review.status === 'approved' ? 'Accepted' : titleize(review.status)}</strong></div>
                <div><span>Reason</span><strong>{review.decision_reason || '-'}</strong></div>
                {review.decision_notes && <div><span>Notes</span><strong>{review.decision_notes}</strong></div>}
                {review.decided_at && <div><span>Recorded</span><strong>{compactDate(review.decided_at)}</strong></div>}
              </div>
            ) : payload.can_decide === false ? (
              <p role="status">Only the assigned attorney or an administrator can record this decision.</p>
            ) : <div className="intake-attorney-review-decision-form">
              <TextArea label="Reason" value={form.reason} rows={3} disabled={Boolean(savingStatus)} onChange={(value) => { setFormError(''); setForm((current) => ({ ...current, reason: value })); }} />
              <TextArea label="Notes" value={form.notes} rows={3} disabled={Boolean(savingStatus)} onChange={(value) => setForm((current) => ({ ...current, notes: value }))} />
              {formError && <div className="intake-form-error" role="alert">{formError}</div>}
              <div className="intake-attorney-review-decision-actions">
                {DECISION_ACTIONS.map((action) => (
                  <button
                    type="button"
                    className={`intake-button ${action.className}`}
                    disabled={Boolean(savingStatus)}
                    key={action.status}
                    onClick={() => submitDecision(action.status)}>
                    <AppIcon icon={savingStatus === action.status ? 'pi pi-spin pi-spinner' : action.icon} aria-hidden="true" />
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>}
          </section>
        </main>
      </div>
  );

  if (isEmbedded) {
    return detailBody;
  }

  return (
    <div className="intake-page intake-attorney-review-detail-page">
      <div className="intake-page__body">
        {detailBody}
      </div>
    </div>
  );
}



export function IntakeAttorneyReviewDetail() {
  const { reviewId } = useParams();
  return <IntakeAttorneyReviewDetailContent reviewId={reviewId} />;
}

export default IntakeAttorneyReviews;
